import { FC } from "react"

import { useTranslation } from "react-i18next"

import { HtmlEditor } from "components/editors/HtmlEditor"

import { CmsFaqEntry, CmsPage } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { Field, FieldRow, ghostButtonClass, inputClass, SectionHeading, ToggleRow } from "../CmsUi"
import { readingMinutes, slugify, wordCount } from "../helpers/format"

interface ContentPaneProps {
  page: CmsPage
  onChange: (patch: Partial<CmsPage>) => void
  slugWarning: string | null
}

export const ContentPane: FC<ContentPaneProps> = ({ page, onChange, slugWarning }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const setFaq = (index: number, patch: Partial<CmsFaqEntry>) =>
    onChange({ faq: page.faq.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)) })

  return (
    <div>
      <FieldRow>
        <Field
          label={t("cmsSeo.field.title", { defaultValue: "Page title" })}
          counter={
            <span className="text-[11.5px] text-gray-400">{t("cmsSeo.field.titleNote", { defaultValue: "internal + fallback" })}</span>
          }
        >
          <input className={inputClass} value={page.title} onChange={(event) => onChange({ title: event.target.value })} />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.slug", { defaultValue: "Slug" })}
          hint={
            slugWarning ?? (
              <>
                {t("cmsSeo.field.slugHint", {
                  defaultValue:
                    "Changing a published slug offers to create a 301 redirect from the old URL. Leave that on unless you know the old URL has no traffic."
                })}
              </>
            )
          }
        >
          <div className="flex items-stretch overflow-hidden rounded-lg border border-black/[0.12] bg-white dark:border-white/[0.12] dark:bg-[#0C0C1A]">
            <span className="flex items-center border-r border-black/[0.1] bg-black/[0.03] px-3 font-mono text-xs text-gray-500 dark:border-white/[0.1] dark:bg-white/[0.04] dark:text-gray-400">
              {site.host}/{page.locale}/
            </span>
            <input
              className="flex-1 bg-transparent px-3 py-2 font-mono text-xs text-black outline-none dark:text-white"
              value={page.slug}
              onChange={(event) => onChange({ slug: event.target.value })}
              onBlur={(event) => onChange({ slug: slugify(event.target.value) })}
            />
          </div>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.h1", { defaultValue: "H1 heading" })}
          hint={t("cmsSeo.field.h1Hint", {
            defaultValue: "Rendered as the page's single <h1>. Keep it different from the title tag."
          })}
        >
          <input className={inputClass} value={page.h1} onChange={(event) => onChange({ h1: event.target.value })} />
        </Field>
      </FieldRow>

      <SectionHeading>{t("cmsSeo.field.body", { defaultValue: "Body" })}</SectionHeading>

      <HtmlEditor value={page.body_html} onChange={(html) => onChange({ body_html: html })} minHeight={280} />

      <div className="flex gap-5 px-1 py-2 text-xs text-gray-400 dark:text-gray-500">
        <span>{t("cmsSeo.words", { defaultValue: "{{count}} words", count: wordCount(page.body_html) })}</span>
        <span>{t("cmsSeo.readTime", { defaultValue: "{{count}} min read", count: readingMinutes(page.body_html) })}</span>
      </div>

      <SectionHeading>{t("cmsSeo.faq", { defaultValue: "FAQ block — feeds FAQPage schema" })}</SectionHeading>

      {page.faq.map((entry, index) => (
        <FieldRow key={index}>
          <Field label={t("cmsSeo.faqQuestion", { defaultValue: "Question" })}>
            <input className={inputClass} value={entry.question} onChange={(event) => setFaq(index, { question: event.target.value })} />
          </Field>
          <Field label={t("cmsSeo.faqAnswer", { defaultValue: "Answer" })}>
            <textarea
              className={inputClass}
              rows={2}
              value={entry.answer}
              onChange={(event) => setFaq(index, { answer: event.target.value })}
            />
          </Field>
          <div className="self-end pb-1">
            <button
              type="button"
              className="btn btn-xs btn-ghost text-rose-500 normal-case"
              onClick={() => onChange({ faq: page.faq.filter((_, i) => i !== index) })}
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </button>
          </div>
        </FieldRow>
      ))}

      <button type="button" className={ghostButtonClass} onClick={() => onChange({ faq: [...page.faq, { question: "", answer: "" }] })}>
        {t("cmsSeo.addFaq", { defaultValue: "+ Add FAQ entry" })}
      </button>

      <SectionHeading>{t("cmsSeo.footerCopy", { defaultValue: "SEO copy below the page" })}</SectionHeading>

      <p className="mb-3 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
        {t("cmsSeo.footerCopyHint", {
          defaultValue:
            "Rendered under the page's own content. The text is in the page source whichever state it starts in, so search engines read it either way — collapsing only keeps it out of a player's way."
        })}
      </p>

      <HtmlEditor value={page.footer_html} onChange={(html) => onChange({ footer_html: html })} minHeight={160} />

      <div className="mt-3">
        <ToggleRow
          title={t("cmsSeo.footerCollapsed", { defaultValue: "Start collapsed" })}
          description={t("cmsSeo.footerCollapsedHint", {
            defaultValue: "Off shows the copy expanded on arrival"
          })}
          checked={page.footer_collapsed}
          onChange={() => onChange({ footer_collapsed: !page.footer_collapsed })}
        />
      </div>
    </div>
  )
}
